import React, { useEffect, useState, useCallback, useRef, useImperativeHandle } from "react";
import { Input, Textarea } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { EmployeeDetails } from "../../components/ui/cards";
import { EMPLOYEE_ATTENDANCE_DAILY } from "../../constants/api";
import useApiMethods from "../../lib/useApiMethods";
import dayjs from "dayjs";
import ToastMsg from "../../components/common/ToastMsg";
import { Calendar, Clock, User, Building, MapPin, CircleQuestionMark, AlertTriangle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../components/ui/tooltip";
import { Checkbox, Select, DatePicker, TimePicker } from "antd";
import { cn } from "../../utils/tailwindUtils";
import { SegmentSwitch } from "../../components/ui/segmentSwitch";
import { useNavigate } from "react-router-dom";

// Attendance status options
const attendanceStatus = [
    { value: "present", text: "Present" },
    { value: "absent", text: "Absent" },
    { value: "on-duty", text: "On Duty" },
    { value: "holiday", text: "Holiday" },
    { value: "week-off", text: "Week Off" },
    { value: "WFH", text: "Work From Home" },
    { value: "leave", text: "Leave" },
    { value: "compensation", text: "Compensation" },
];

const CORRECTION_TYPE_OPTIONS = [
    { value: "single_punch", label: "Single Punch" },
    { value: "late", label: "Late Attendance" },
    { value: "missing_punch", label: "Missing Punch / Short Duty" },
    { value: "status_change", label: "Status Change" },
    { value: "other", label: "Other" },
];

// Filter option for Select
const filterOption = (input, option) =>
    option.children.toLowerCase().includes(input.toLowerCase());

const PENALTY_STATUS_STYLES = {
    applied: "bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300",
    suggested: "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300",
    within_grace: "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300",
    disabled: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
    none: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
};

const PenaltySummarySection = ({ summary, standalone = false }) => {
    const hasPenalties = summary && (summary.late || summary.short_duty);

    if (!hasPenalties) {
        if (!standalone) return null;
        return (
            <div className="p-8 border border-dashed border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50/50 dark:bg-gray-900/30 text-center">
                <AlertTriangle className="w-8 h-8 mx-auto text-gray-400 dark:text-gray-500 mb-3" />
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">No penalties for this day</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Late arrival and short-duty penalties will appear here when applicable.
                </p>
            </div>
        );
    }

    const renderLate = () => {
        const late = summary.late;
        if (!late) return null;
        const status = late.penalty_status || "none";
        return (
            <div className="rounded-lg border border-orange-200 dark:border-orange-800/60 bg-orange-50/50 dark:bg-orange-900/10 p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-orange-800 dark:text-orange-300 uppercase">Late Arrival</span>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${PENALTY_STATUS_STYLES[status] || PENALTY_STATUS_STYLES.none}`}>
                        {status === "applied" ? "Applied" : status === "suggested" ? "Suggested" : status === "within_grace" ? "Within grace" : status}
                    </span>
                </div>
                {late.late_minutes_after_grace != null && (
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                        {late.late_minutes_after_grace} min after check-in grace
                    </p>
                )}
                {late.penalty_label && (
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        {late.penalty_label}: {late.penalty_value || late.penalty_display}
                    </p>
                )}
                {late.grace_status && late.grace_status !== "-" && (
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">{late.grace_status}</p>
                )}
            </div>
        );
    };

    const renderShortDuty = () => {
        const sd = summary.short_duty;
        if (!sd) return null;
        const status = sd.is_suggested ? "suggested" : sd.review_status === "Reviewed" ? "applied" : "within_grace";
        return (
            <div className="rounded-lg border border-amber-200 dark:border-amber-800/60 bg-amber-50/50 dark:bg-amber-900/10 p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-amber-800 dark:text-amber-300 uppercase">Short Duty</span>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${PENALTY_STATUS_STYLES[status] || PENALTY_STATUS_STYLES.none}`}>
                        {sd.review_status || "Pending"}
                    </span>
                </div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {sd.penalty_label || (sd.mode === "fine" ? "Fine" : "Deduction")}: {sd.penalty_display || sd.penalty_value}
                </p>
                {sd.description && (
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">{sd.description}</p>
                )}
            </div>
        );
    };

    return (
        <div className={cn(
            "space-y-4",
            standalone ? "" : "p-4 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800",
        )}>
            {!standalone && (
                <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className="w-3 h-3 text-rose-600 dark:text-rose-400" />
                    <h3 className="text-xs font-semibold text-gray-900 dark:text-white uppercase">Penalty Details</h3>
                </div>
            )}
            <div className="space-y-3">
                {renderLate()}
                {renderShortDuty()}
            </div>
            <p className="text-[10px] text-gray-400 dark:text-gray-500">
                Suggested amounts follow Payroll Processing defaults until HR finalizes in Attendance Penalties.
            </p>
        </div>
    );
};

const AttendanceEdit = React.forwardRef(({ editDetails, onSuccess }, ref) => {
    const { update, fetch } = useApiMethods();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isApplyShift, setIsApplyShift] = useState(false);
    const [errors, setErrors] = useState({});
    const [activeTab, setActiveTab] = useState("attendance");
    const hideCursorRef = useRef();
    const [penaltySummary, setPenaltySummary] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        id: null,
        date: "",
        employeeDetails: {},
        shift: "",
        department: "",
        location: "",
        status: "",
        check_in: "",
        check_out: "",
        over_time: "00:00:00",
        break_hrs: "00:00:00",
        working_hrs: "00:00:00",
        description: "",
        correction_type: "other",
        is_short_duty: false,
        shift_working_hours: "",
    });

    // Format time span helper function
    const formatTimeSpan = (input) => {
        if (!input || input === "NaT") return "00:00:00";

        if (typeof input === "string") {
            if (input.includes("days") || input.includes("day")) {
                const [daysPart, timePart] = input.split(", ");
                const [daysStr] = daysPart.split(" ");
                const days = parseInt(daysStr, 10) || 0;
                const [hours, minutes, seconds] = timePart.split(/[:,]/).map(Number);
                const totalHours = days * 24 + (hours || 0);
                return `${String(totalHours).padStart(2, "0")}:${String(minutes || 0).padStart(2, "0")}:${String(seconds || 0).padStart(2, "0")}`;
            }
            return input;
        }
        return "00:00:00";
    };

    // Initialize form data when editDetails changes
    useEffect(() => {
        if (editDetails) {
            const { employee_details } = editDetails;

            const empDetails = {
                title: employee_details?.title || "",
                first_name: employee_details?.employee_name || "",
                last_name: "",
                designation_id: {
                    id: employee_details?.designation?.id || "",
                    designation_name: employee_details?.designation?.name || ""
                },
                id: employee_details?.employee_id,
                profile: employee_details?.profile,
            };

            const formattedData = {
                id: editDetails?.attendance_id || null,
                date: editDetails?.date ? dayjs(editDetails.date).format("DD/MM/YYYY") : "",
                employeeDetails: empDetails,
                shift: editDetails?.shift || "",
                department: employee_details?.department?.name || "",
                location: editDetails?.location || "",
                status: editDetails?.status || "",
                check_in: (editDetails?.check_in && editDetails.check_in !== "NaT")
                    ? dayjs(editDetails.check_in).startOf("minute").format("YYYY-MM-DDTHH:mm:ss")
                    : "",
                check_out: (editDetails?.check_out && editDetails.check_out !== "NaT")
                    ? dayjs(editDetails.check_out).startOf("minute").format("YYYY-MM-DDTHH:mm:ss")
                    : "",
                over_time: (editDetails?.overtime && editDetails.overtime !== "NaT")
                    ? formatTimeSpan(editDetails.overtime)
                    : "00:00:00",
                break_hrs: (editDetails?.break_hrs && editDetails.break_hrs !== "NaT")
                    ? dayjs(editDetails.break_hrs, "H:mm:ss").format("HH:mm:ss")
                    : "00:00:00",
                working_hrs: (editDetails?.total_working_hours && editDetails.total_working_hours !== "NaT")
                    ? formatTimeSpan(editDetails.total_working_hours)
                    : "00:00:00",
                description: editDetails?.description || "",
                correction_type: editDetails?.correction_type || "other",
                is_short_duty: Boolean(editDetails?.is_short_duty),
                shift_working_hours: (editDetails?.shift_working_hours && editDetails.shift_working_hours !== "NaT")
                    ? dayjs(editDetails.shift_working_hours, "H:mm:ss").format("HH:mm:ss")
                    : "00:00:00",
            };

            setFormData(formattedData);
            setIsEditMode(true);
            setPenaltySummary(editDetails?.penalty_summary || null);
            setActiveTab("attendance");
        }
    }, [editDetails]);

    const fetchRef = useRef(fetch);
    fetchRef.current = fetch;

    useEffect(() => {
        if (editDetails?.penalty_summary) {
            setPenaltySummary(editDetails.penalty_summary);
            return;
        }

        const employeeId = editDetails?.employee_details?.id;
        const dateRaw = editDetails?.date;
        if (!employeeId || !dateRaw) {
            setPenaltySummary(null);
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                const date = dayjs(dateRaw).format("YYYY-MM-DD");
                const response = await fetchRef.current(EMPLOYEE_ATTENDANCE_DAILY, { employee_id: employeeId, date });
                if (cancelled) return;
                const row = response?.data?.result?.[0] || response?.data?.[0];
                setPenaltySummary(row?.penalty_summary || null);
            } catch {
                if (!cancelled) setPenaltySummary(null);
            }
        })();

        return () => { cancelled = true; };
    }, [editDetails]);

    // Prepare employee details for display
    const empDetails = React.useMemo(() => {
        if (!editDetails?.employee_details) return {};

        return {
            ...editDetails.employee_details,
            designation_id: editDetails.employee_details?.designation
                ? {
                    id: editDetails.employee_details.designation.id,
                    designation_name: editDetails.employee_details.designation.name,
                }
                : { id: 0, designation_name: "" },
            first_name: editDetails.employee_details?.employee_name || "-",
            id: editDetails.employee_details?.employee_id || "-",
        };
    }, [editDetails]);

    // Helper functions for time calculations
    const timeToSeconds = (timeStr) => {
        if (!timeStr) return 0;
        const [hours, minutes, seconds] = timeStr.split(":").map(Number);
        return (hours || 0) * 3600 + (minutes || 0) * 60 + (seconds || 0);
    };

    const secondsToTime = (totalSeconds) => {
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    };

    const resolveShiftEndDateTime = (checkIn, checkOut, dateStr, shiftDetails, shiftCheckOutRaw) => {
        if (shiftCheckOutRaw) {
            const parsed = dayjs(shiftCheckOutRaw);
            if (parsed.isValid()) return parsed;
        }
        if (!shiftDetails?.shift_timings_to || !dateStr) return null;

        const dateOnly = dayjs(dateStr, "DD/MM/YYYY");
        if (!dateOnly.isValid()) return null;

        const shiftFrom = shiftDetails.shift_timings_from;
        const shiftTo = shiftDetails.shift_timings_to;
        const isNightShift = shiftFrom && shiftTo && shiftFrom > shiftTo;

        let shiftEnd = dayjs(`${dateOnly.format("YYYY-MM-DD")}T${shiftTo}`);
        if (isNightShift) {
            shiftEnd = shiftEnd.add(1, "day");
        }
        return shiftEnd;
    };

    // Calculate working hours
    const calculateWorkingHours = useCallback(() => {
        if (!formData.check_in || !formData.check_out) {
            setFormData(prev => ({
                ...prev,
                working_hrs: "00:00:00",
                over_time: "00:00:00"
            }));
            return;
        }

        try {
            const checkIn = dayjs(formData.check_in).startOf("minute");
            let checkOut = dayjs(formData.check_out).startOf("minute");

            // Handle night shifts (check-out next day)
            if (checkOut.isBefore(checkIn)) {
                checkOut = checkOut.add(1, "day");
            }

            const totalSeconds = checkOut.diff(checkIn, "seconds");
            const breakSeconds = timeToSeconds(formData.break_hrs);
            const netSeconds = Math.max(totalSeconds - breakSeconds, 0);

            const shiftEnd = resolveShiftEndDateTime(
                checkIn,
                checkOut,
                formData.date,
                editDetails?.shiftDetails,
                editDetails?.shift_check_out,
            )?.startOf("minute");

            let overtimeSeconds = 0;
            if (shiftEnd && checkOut.isAfter(shiftEnd)) {
                overtimeSeconds = checkOut.diff(shiftEnd, "seconds");
            } else if (!shiftEnd) {
                const shiftSeconds = timeToSeconds(formData.shift_working_hours);
                overtimeSeconds = Math.max(netSeconds - shiftSeconds, 0);
            }

            setFormData(prev => ({
                ...prev,
                working_hrs: secondsToTime(netSeconds),
                over_time: secondsToTime(overtimeSeconds)
            }));
        } catch (error) {
            console.error("Error calculating working hours:", error);
        }
    }, [
        formData.check_in,
        formData.check_out,
        formData.break_hrs,
        formData.shift_working_hours,
        formData.date,
        editDetails?.shiftDetails,
        editDetails?.shift_check_out,
    ]);

    // Recalculate when dependencies change
    useEffect(() => {
        calculateWorkingHours();
    }, [calculateWorkingHours]);

    // Handle form field changes
    const handleFieldChange = (field, value) => {
        setFormData(prev => {
            const next = { ...prev, [field]: value };
            if (field === "correction_type" && value === "missing_punch") {
                next.is_short_duty = true;
                next.check_out = "";
            }
            return next;
        });

        // Clear errors when field is filled
        if (value && errors[field]) {
            setErrors(prev => ({ ...prev, [field]: "" }));
        }
        if (field === "correction_type" && value === "missing_punch" && errors.check_out) {
            setErrors(prev => ({ ...prev, check_out: "" }));
        }
    };

    const handleShortDutyToggle = (e) => {
        const checked = e.target.checked;
        setIsApplyShift(false);
        setFormData(prev => ({
            ...prev,
            is_short_duty: checked,
            check_out: checked ? "" : prev.check_out,
            correction_type: checked && prev.correction_type === "other" ? "missing_punch" : prev.correction_type,
        }));
        if (checked && errors.check_out) {
            setErrors(prev => ({ ...prev, check_out: "" }));
        }
    };

    // Handle date time changes for check in/out
    const handleApplyShiftToggle = (e) => {
        const isChecked = e.target.checked;
        setIsApplyShift(isChecked);

        if (isChecked) {
            const shift = editDetails?.shiftDetails;

            const dateOnly = formData?.date
                ? dayjs(formData.date, "DD/MM/YYYY").isValid()
                    ? dayjs(formData.date, "DD/MM/YYYY")
                    : null
                : null;

            if (dateOnly && shift?.shift_timings_from && shift?.shift_timings_to) {
                const shiftFrom = shift.shift_timings_from; // e.g. "22:00:00"
                const shiftTo = shift.shift_timings_to;     // e.g. "06:00:00"
                const isNightShift = shiftFrom > shiftTo;   // Simple string comparison works here

                const checkInDateTime = dayjs(`${dateOnly.format("YYYY-MM-DD")}T${shiftFrom}`);
                let checkOutDateTime = dayjs(`${dateOnly.format("YYYY-MM-DD")}T${shiftTo}`);

                if (isNightShift) {
                    checkOutDateTime = checkOutDateTime.add(1, 'day');  // Move to next day
                    ToastMsg("warning", "Applied night shift timings across dates.");
                }

                setFormData((prev) => ({
                    ...prev,
                    status: "present",
                    check_in: checkInDateTime.format("YYYY-MM-DDTHH:mm:ss"),
                    ...(prev.is_short_duty
                        ? {}
                        : { check_out: checkOutDateTime.format("YYYY-MM-DDTHH:mm:ss") }),
                    description: `Applied shift timings`,
                }));
            }
        } else {
            // Optionally clear check-in/out when unchecked
            setFormData((prev) => ({
                ...prev,
                check_in: null,
                check_out: null,
            }));
        }
    };

    // Update the handleDateTimeChange function to handle dayjs objects properly
    const handleDateTimeChange = (field, value) => {
        if (value && value.isValid()) {
            const dateTimeValue = value.startOf("minute").format("YYYY-MM-DDTHH:mm:ss");
            handleFieldChange(field, dateTimeValue);
        } else {
            handleFieldChange(field, "");
        }
    };

    // Handle time changes for break hours and shift hours
    const handleTimeChange = (field, value) => {
        const timeValue = value ? value.format("HH:mm:ss") : "00:00:00";
        handleFieldChange(field, timeValue);
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        if (!formData.status) {
            newErrors.status = "Status is required";
        }

        const reasonText = (formData.description || "").trim();
        if (reasonText.length < 10) {
            newErrors.description = "Correction reason must be at least 10 characters";
        }

        if (!formData.correction_type) {
            newErrors.correction_type = "Correction type is required";
        }

        if (formData.status === "present" || formData.status === "on-duty") {
            if (!formData.check_in) {
                newErrors.check_in = "Check-in time is required";
            }
            if (!formData.is_short_duty && !formData.check_out) {
                newErrors.check_out = "Check-out time is required";
            }
            if (formData.check_in && formData.check_out && !formData.is_short_duty) {
                const checkIn = dayjs(formData.check_in);
                const checkOut = dayjs(formData.check_out);
                if (checkOut.isBefore(checkIn)) {
                    newErrors.check_out = "Check-out time cannot be before check-in time";
                }
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submission
    useImperativeHandle(ref, () => ({
        handleSubmit: async () => {
            if (!validateForm()) {
                ToastMsg("error", "Please fix the errors before submitting");
                throw new Error("Validation failed");
            }

            try {
                setLoading(true);
                const payload = {
                    id: formData.id,
                    status: formData.status,
                    check_in: formData.check_in
                        ? dayjs(formData.check_in).format("YYYY-MM-DD HH:mm:ss")
                        : null,
                    check_out: formData.check_out
                        ? dayjs(formData.check_out).format("YYYY-MM-DD HH:mm:ss")
                        : null,
                    working_hrs: formData.working_hrs || "00:00:00",
                    over_time: formData.over_time || "00:00:00",
                    break_hrs: formData.break_hrs || "00:00:00",
                    correction_reason: formData.description || "",
                    description: formData.description || "",
                    correction_type: formData.correction_type || "other",
                    is_short_duty: Boolean(formData.is_short_duty),
                };

                const response = await update(EMPLOYEE_ATTENDANCE_DAILY, payload, false);

                if (response?.status === "success") {
                    ToastMsg("success", response.message || "Attendance updated successfully");
                    onSuccess?.();
                    return response;
                }

                const message = response?.message || "Failed to update attendance";
                ToastMsg("error", message);
                throw new Error(message);
            } catch (error) {
                console.error("Update error:", error);
                throw error;
            } finally {
                setLoading(false);
            }
        }
    }));


    // Check if time fields are required
    const isTimeRequired = formData.status === "present" || formData.status === "on-duty";
    const isCheckOutRequired = isTimeRequired && !formData.is_short_duty;
    const penaltyCount = (penaltySummary?.late ? 1 : 0) + (penaltySummary?.short_duty ? 1 : 0);

    const tabOptions = [
        { value: "attendance", label: "Attendance Details" },
        {
            value: "penalty",
            label: penaltyCount ? `Penalty Details (${penaltyCount})` : "Penalty Details",
        },
    ];

    return (
        <div className="h-[calc(100vh-300px)] flex flex-col">
            <div className="px-2 pb-3 mb-1 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3">
                <SegmentSwitch
                    options={tabOptions}
                    value={activeTab}
                    onChange={setActiveTab}
                />
                {activeTab === "penalty" && (
                    <button
                        type="button"
                        onClick={() => navigate("/payroll/attendancepenalties")}
                        className="text-xs font-medium text-sidebar-primary hover:underline"
                    >
                        Open Attendance Penalties
                    </button>
                )}
            </div>

            <div className="flex-1 overflow-y-auto">
                {activeTab === "attendance" ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-2">

                    {/* LEFT COLUMN */}
                    <div className="space-y-6">
                        {/* Employee & Work Info */}
                        <div className="p-4 bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm">
                            <div className="grid grid-cols-1 gap-6">

                                {/* Employee Info */}
                                <div>
                                    <div className="flex items-center gap-2 mb-3 border-b border-gray-200 dark:border-gray-700 pb-2">
                                        <User className="w-4 h-4 text-sidebar dark:text-blue-400" />
                                        <h3 className="text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
                                            Employee Information
                                        </h3>
                                    </div>
                                    <div className=" origin-left">
                                        <EmployeeDetails details={empDetails} size="xs" />
                                    </div>
                                </div>

                                {/* Work Details */}
                                <div>
                                    <div className="flex items-center gap-2 mb-3 border-b border-gray-200 dark:border-gray-700 pb-2">
                                        <Building className="w-4 h-4 text-green-600 dark:text-green-400" />
                                        <h3 className="text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
                                            Work Details
                                        </h3>
                                    </div>

                                    <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                                        {/* Shift */}
                                        <div className="space-y-1">
                                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400 flex items-center gap-1">
                                                Shift
                                                <Tooltip>
                                                    <TooltipTrigger>
                                                        <CircleQuestionMark className="w-3 h-3 cursor-pointer text-gray-400 hover:text-gray-600" />
                                                    </TooltipTrigger>
                                                    <TooltipContent side="top" className="text-xs max-w-xs">
                                                        {editDetails?.shiftDetails ? (
                                                            <div className="space-y-1">
                                                                <p><strong>Timings:</strong> {editDetails.shiftDetails.shift_timings_from} - {editDetails.shiftDetails.shift_timings_to}</p>
                                                                <p><strong>Working Hours:</strong> {formData.shift_working_hours}</p>
                                                            </div>
                                                        ) : (
                                                            <p>No shift details available</p>
                                                        )}
                                                    </TooltipContent>
                                                </Tooltip>
                                            </label>
                                            <p className="text-xs text-gray-900 dark:text-white font-medium">{formData.shift || "-"}</p>
                                        </div>

                                        {/* Department */}
                                        <div className="space-y-1">
                                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Department</label>
                                            <p className="text-xs text-gray-900 dark:text-white font-medium">{formData.department || "-"}</p>
                                        </div>

                                        {/* Location */}
                                        <div className="space-y-1">
                                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Location</label>
                                            <p className="text-xs text-gray-900 dark:text-white font-medium flex items-center gap-1">
                                                <MapPin className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                                                {formData.location || "-"}
                                            </p>
                                        </div>

                                        {/* Date */}
                                        <div className="space-y-1">
                                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Date</label>
                                            <p className="text-xs text-gray-900 dark:text-white font-medium flex items-center gap-1">
                                                <Calendar className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                                                {formData.date}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Time Details */}
                        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800">
                            <div className="flex items-center gap-2 mb-3">
                                <Clock className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                                <h3 className="text-xs font-semibold text-gray-900 dark:text-white uppercase">Time Details</h3>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {/* Check-In */}
                                <div className="flex flex-col gap-1">
                                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                        Check-In {isTimeRequired && <span className="text-red-500">*</span>}
                                    </label>
                                    <DatePicker
                                        showTime
                                        format="DD-MM-YYYY hh:mm A"
                                        value={formData.check_in ? dayjs(formData.check_in) : null}
                                        onChange={(value) => handleDateTimeChange("check_in", value)}
                                        disabled={!isEditMode || !isTimeRequired}
                                        className={cn("w-full", errors.check_in ? "border-red-500" : "")}
                                        size="small"
                                    />
                                    {errors.check_in && (
                                        <p className="text-red-500 text-xs mt-1 flex items-center gap-1">⚠️ {errors.check_in}</p>
                                    )}
                                </div>

                                {/* Check-Out */}
                                <div className="flex flex-col gap-1">
                                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                        Check-Out {isCheckOutRequired && <span className="text-red-500">*</span>}
                                        {formData.is_short_duty && (
                                            <span className="text-gray-400 font-normal ml-1">(not required for short duty)</span>
                                        )}
                                    </label>
                                    <DatePicker
                                        showTime
                                        format="DD-MM-YYYY hh:mm A"
                                        value={formData.check_out ? dayjs(formData.check_out) : null}
                                        onChange={(value) => handleDateTimeChange("check_out", value)}
                                        disabled={!isEditMode || !isTimeRequired || formData.is_short_duty}
                                        className={cn("w-full", errors.check_out ? "border-red-500" : "")}
                                        size="small"
                                    />
                                    {errors.check_out && (
                                        <p className="text-red-500 text-xs mt-1 flex items-center gap-1">⚠️ {errors.check_out}</p>
                                    )}
                                </div>

                                {/* Break Hours */}
                                <div className="flex flex-col gap-1">
                                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Break Hours</label>
                                    <TimePicker
                                        format="HH:mm:ss"
                                        value={formData.break_hrs ? dayjs(formData.break_hrs, "HH:mm:ss") : null}
                                        onChange={(value) => handleTimeChange("break_hrs", value)}
                                        disabled={!isEditMode}
                                        className="w-full"
                                        size="small"
                                    />
                                </div>

                                {/* Shift Working Hours */}
                                <div className="flex flex-col gap-1">
                                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Shift Hours</label>
                                    <TimePicker
                                        format="HH:mm:ss"
                                        value={formData.shift_working_hours ? dayjs(formData.shift_working_hours, "HH:mm:ss") : null}
                                        onChange={(value) => handleTimeChange("shift_working_hours", value)}
                                        disabled={!isEditMode}
                                        className="w-full"
                                        size="small"
                                    />
                                </div>
                            </div>

                            {/* Calculated Hours */}
                            <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                                <div className="flex flex-col gap-1 text-center p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                                    <label className="text-xs font-medium text-blue-700 dark:text-blue-300">Working Hours</label>
                                    <p className="text-xs font-semibold text-blue-900 dark:text-blue-100">{formData.working_hrs}</p>
                                </div>
                                <div className="flex flex-col gap-1 text-center p-2 bg-green-50 dark:bg-green-900/20 rounded">
                                    <label className="text-xs font-medium text-green-700 dark:text-green-300">Overtime</label>
                                    <p className="text-xs font-semibold text-green-900 dark:text-green-100">{formData.over_time}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT COLUMN */}
                    <div className="space-y-6">
                        {/* Attendance Status */}
                        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800">
                            <div className="flex items-center gap-2 mb-3">
                                <Calendar className="w-3 h-3 text-purple-600 dark:text-purple-400" />
                                <h3 className="text-xs font-semibold text-gray-900 dark:text-white uppercase">Attendance Status</h3>
                            </div>

                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                    Status <span className="text-red-500">*</span>
                                </label>
                                <Select
                                    showSearch
                                    size="Large"
                                    className={cn(
                                        "w-full rounded-md transition-colors duration-200",
                                        errors.status ? "border-red-500" : "border-gray-300 dark:border-gray-600",
                                        "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                    )}
                                    value={formData.status || ""}
                                    onChange={(value) => {
                                        const updatedFormData = {
                                            ...formData,
                                            status: value,
                                            ...(value === "absent"
                                                ? { check_in: null, check_out: null, is_short_duty: false }
                                                : {}),
                                        };
                                        setIsApplyShift(false);
                                        setFormData(updatedFormData);
                                        if (value) {
                                            setErrors((prevState) => ({
                                                ...prevState,
                                                status: ""
                                            }));
                                        }
                                    }}
                                    filterOption={filterOption}
                                    ref={hideCursorRef}
                                    onBlur={() => hideCursorRef.current?.blur()}
                                    disabled={!isEditMode}
                                >
                                    {attendanceStatus.map((option) => (
                                        <Select.Option key={option.value} value={option.value}>
                                            {option.text}
                                        </Select.Option>
                                    ))}
                                </Select>
                                {errors.status && (
                                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">⚠️ {errors.status}</p>
                                )}
                            </div>

                            <div className="flex justify-between items-center mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                                <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Time Tracker</p>
                                {isEditMode && (
                                    <div className="flex flex-wrap gap-4 items-center">
                                        <div className="flex gap-2 items-center">
                                            <Checkbox onChange={handleShortDutyToggle} checked={formData.is_short_duty} />
                                            <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Short duty (missing checkout)</p>
                                        </div>
                                        <div className="flex gap-2 items-center">
                                            <Checkbox onChange={handleApplyShiftToggle} checked={isApplyShift} />
                                            <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Apply shift timing</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Correction meta */}
                        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800">
                            <div className="flex flex-col gap-3">
                                <div className="flex flex-col gap-2">
                                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                        Correction Type <span className="text-red-500">*</span>
                                    </label>
                                    <Select
                                        size="large"
                                        className={cn("w-full", errors.correction_type ? "border-red-500" : "")}
                                        value={formData.correction_type || undefined}
                                        onChange={(value) => handleFieldChange("correction_type", value)}
                                        disabled={!isEditMode}
                                        options={CORRECTION_TYPE_OPTIONS}
                                    />
                                    {errors.correction_type && (
                                        <p className="text-red-500 text-xs mt-1">⚠️ {errors.correction_type}</p>
                                    )}
                                </div>
                                <div className="flex flex-col gap-2">
                                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                        Correction Reason <span className="text-red-500">*</span>
                                    </label>
                                    <Textarea
                                        value={formData.description}
                                        onChange={(e) => handleFieldChange("description", e.target.value)}
                                        disabled={!isEditMode}
                                        placeholder="Enter the reason/excuse for this attendance correction (min 10 characters)..."
                                        rows={4}
                                        showCharCount={true}
                                        maxLength={500}
                                        className={cn(
                                            "w-full rounded-md transition-colors duration-200",
                                            errors.description ? "border-red-500" : "border-gray-300 dark:border-gray-600",
                                        )}
                                    />
                                    {errors.description && (
                                        <p className="text-red-500 text-xs mt-1">⚠️ {errors.description}</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Description */}
                        <div className="hidden p-4 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800">
                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Description</label>
                                <Textarea
                                    value={formData.description}
                                    onChange={(e) => handleFieldChange("description", e.target.value)}
                                    disabled={!isEditMode}
                                    placeholder="Enter any additional notes or description..."
                                    rows={4}
                                    showCharCount={true}
                                    maxLength={250}
                                    className={cn(
                                        "w-full rounded-md transition-colors duration-200",
                                        "border-gray-300 dark:border-gray-600",
                                        "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                ) : (
                    <div className="p-2 w-full">
                        <PenaltySummarySection summary={penaltySummary} standalone />
                    </div>
                )}
            </div>
        </div>
    );
});

export default AttendanceEdit;